/**
 * The send flow's decisions, shown where they can be checked (WP-CH-12).
 *
 * It lives on this screen on purpose. The Console is not wired to it yet, and
 * putting the logic behind a panel here means it can be seen working before it
 * is put in front of a customer's send.
 */

import React from 'react';
import { Panel, Select } from '@cloudsam/ui';

const FAILURE_CODES = [
    'NO_BINDING', 'BINDING_PAUSED', 'WITHDRAWN', 'IDENTITY_BUSY', 'NO_TEMPLATE',
    'NO_RATE', 'PROVIDER_REFUSED', 'INVALID_ADDRESS', 'RATE_LIMITED',
];

// Trailing zeros are dropped so a price reads the way it was published. Money
// per message can be smaller than a cent, so four places are kept.
function money(n) {
    if (n === null || n === undefined) return '—';
    return `$${n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}`;
}

export default function SendPreviewPanel({
    rows = [], selected, onSelect, eligibility, price,
    failureCode = 'NO_BINDING', failureExample = {}, onFailureCode,
}) {
    return (
        <Panel title="Send preview" count="WP-CH-12">
            <div className="panel-note">
                This panel is the send flow's thinking, shown here so it can be checked.
                The Console does not use it yet.
            </div>

            <div className="defaults-row">
                <div>
                    <div className="defaults-label">Channel</div>
                    <div className="defaults-hint">
                        Every channel is listed. Those without an active binding are greyed, never hidden.
                    </div>
                </div>
                <div className="stack">
                    {rows.map((row) => (
                        <div key={row.id} className="row-gap">
                            <input
                                type="radio"
                                name="sendpreview-channel"
                                disabled={!row.selectable}
                                checked={selected === row.id}
                                onChange={() => onSelect && onSelect(row.id)}
                            />
                            <span className={row.selectable ? undefined : 'muted'}>{row.name}</span>
                            {row.reason && <span className="muted">{row.reason}</span>}
                        </div>
                    ))}
                </div>
            </div>

            <div className="defaults-row">
                <div>
                    <div className="defaults-label">Recipients</div>
                    <div className="defaults-hint">
                        Checked against the do-not-contact list before anything is sent.
                    </div>
                </div>
                <div className="defaults-controls">
                    {!eligibility ? (
                        <span className="muted">Pick a channel to see who can be reached.</span>
                    ) : (
                        <div className="stack">
                            <div>{eligibility.summary}</div>
                            {eligibility.blockedCount > 0 && eligibility.blocked.map((b, i) => (
                                <div key={b.recipient?.recipient_id || i} className="muted">
                                    {b.recipient?.name || b.recipient?.recipient_id} — {b.reason}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="defaults-row">
                <div>
                    <div className="defaults-label">Estimate</div>
                    <div className="defaults-hint">
                        The message count comes from the renderer's own rules. The money is the rate
                        row times that count.
                    </div>
                </div>
                <div className="defaults-controls">
                    {!price ? (
                        <span className="muted">No estimate yet.</span>
                    ) : !price.priced ? (
                        <span className="muted">{price.note}</span>
                    ) : (
                        <div className="stack">
                            <div>Representative: {money(price.representative)}</div>
                            <div className="muted">{price.note}</div>
                        </div>
                    )}
                </div>
            </div>

            <div className="defaults-row">
                <div>
                    <div className="defaults-label">Failure wording</div>
                    <div className="defaults-hint">
                        What the Send Monitor says when a send does not go through.
                    </div>
                </div>
                <div className="stack">
                    <Select value={failureCode} onChange={onFailureCode} aria-label="Failure code">
                        {FAILURE_CODES.map((code) => <option key={code} value={code}>{code}</option>)}
                    </Select>
                    <div><strong>{failureExample.title}</strong></div>
                    <div className="muted">{failureExample.detail}</div>
                    <div>
                        <span className={`tag ${failureExample.canRetry ? 'tag--yellow' : 'tag--neutral'}`}>
                            {failureExample.canRetry ? 'Can retry' : 'Will not change'}
                        </span>
                    </div>
                </div>
            </div>
        </Panel>
    );
}
